import React, { useCallback, useEffect, useRef, useState } from 'react';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Box, HStack, Text, Textarea, useBreakpointValue } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { DocumentWithDownloadUrl as Document } from '@/client';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import useUpdateTextRecord from '@/hooks/house/documents/useUpdateTextRecord';
import { EASE_DRAWER } from '@/components/ui/motion';

interface DocumentNoteSheetProps {
	document: Document;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	onDocumentUpdated?: () => void;
}

interface NoteFormValues {
	description: string;
}

/**
 * Bottom-sheet composer for editing a note. Single editing surface: the
 * first line is treated as the title in the read view (similar to Apple Notes pattern),
 * but composing is one continuous field.
 */
const DocumentNoteSheet: React.FC<DocumentNoteSheetProps> = ({ document, isOpen, onOpenChange, onDocumentUpdated }) => {
	const sheetHeight = useBreakpointValue({ base: '90%', md: '50%' });
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const requestCloseRef = useRef<() => void>(() => undefined);
	const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
	const latestDescriptionRef = useRef(document.description || '');

	useEffect(() => {
		if (!isOpen) {
			latestDescriptionRef.current = document.description || '';
		}
	}, [document.description, isOpen]);

	const {
		register,
		handleSubmit,
		reset,
		formState: { isValid, isDirty },
	} = useForm<NoteFormValues>({
		mode: 'onChange',
		defaultValues: { description: document.description || '' },
	});

	const { ref: rhfRef, ...rhfRest } = register('description', {
		required: 'Note cannot be empty',
		validate: (value) => value.trim() !== '' || 'Note cannot be empty',
	});

	const { updateDocument, isUpdating } = useUpdateTextRecord<NoteFormValues>({
		document,
		transformData: (data) => data.description,
	});

	const handleRequestClose = useCallback(() => {
		if (isUpdating) return;
		if (isDirty) {
			setIsDiscardDialogOpen(true);
			return;
		}
		onOpenChange(false);
	}, [isDirty, isUpdating, onOpenChange]);

	useEffect(() => {
		requestCloseRef.current = handleRequestClose;
	}, [handleRequestClose]);

	const handleConfirmDiscard = useCallback(() => {
		reset({ description: latestDescriptionRef.current });
		setIsDiscardDialogOpen(false);
		onOpenChange(false);
	}, [onOpenChange, reset]);

	useEffect(() => {
		if (isOpen) {
			reset({ description: latestDescriptionRef.current });
			const id = window.setTimeout(() => textareaRef.current?.focus(), 200);
			return () => window.clearTimeout(id);
		}
	}, [isOpen, reset]);

	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') requestCloseRef.current();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [isOpen]);

	const onSubmit = async (data: NoteFormValues) => {
		await updateDocument(data);
		onDocumentUpdated?.();
		onOpenChange(false);
	};

	return (
		<>
			<Box position="absolute" inset={0} pointerEvents={isOpen ? 'auto' : 'none'} zIndex={2}>
				<Box
					position="absolute"
					inset={0}
					bg="blackAlpha.500"
					opacity={isOpen ? 1 : 0}
					transition="opacity 0.25s ease"
					onClick={handleRequestClose}
				/>

				<Box
					position="absolute"
					left={0}
					right={0}
					bottom={0}
					h={sheetHeight}
					bg="surface.default"
					borderTopRadius="3xl"
					boxShadow="lg"
					display="flex"
					flexDirection="column"
					transform={isOpen ? 'translateY(0)' : 'translateY(100%)'}
					transition={`transform 0.3s ${EASE_DRAWER}`}
					role="dialog"
					aria-label="Edit note"
				>
					<Box pt={2} pb={1} display="flex" justifyContent="center" flexShrink={0}>
						<Box w={10} h={1} borderRadius="full" bg="border.default" aria-hidden="true" />
					</Box>

					<Box px={6} py={2} flexShrink={0}>
						<Text textStyle="label" color="fg.muted" textAlign="center">
							Edit note
						</Text>
					</Box>

					<Box
						as="form"
						onSubmit={handleSubmit(onSubmit)}
						display="flex"
						flexDirection="column"
						flex="1"
						minH="0"
					>
						<Box flex="1" px={6} pb={2} minH="0">
							<Textarea
								ref={(el) => {
									rhfRef(el);
									textareaRef.current = el;
								}}
								{...rhfRest}
								h="100%"
								minH="120px"
								p={0}
								bg="transparent"
								border="none"
								color="brand.fg"
								textStyle="body"
								lineHeight={1.625}
								resize="none"
								placeholder="Start writing..."
								disabled={isUpdating}
								_focus={{ outline: 'none', boxShadow: 'none' }}
								_focusVisible={{ outline: 'none', boxShadow: 'none' }}
								css={{
									// CSS first-line follows visual wrapping, not the first newline-delimited line.
									// If link records merge into notes, avoid title styling for a first-line URL.
									'&::first-line': {
										fontSize: 'var(--chakra-font-sizes-xl)',
										fontWeight: 'var(--chakra-font-weights-semibold)',
									},
								}}
							/>
						</Box>

						<HStack px={6} py={4} gap={3} flexShrink={0}>
							<OutlinedButton size="lg" onClick={handleRequestClose} disabled={isUpdating} flex="1">
								Close
							</OutlinedButton>
							<PrimaryButton
								type="submit"
								size="lg"
								fontWeight="normal"
								loading={isUpdating}
								loadingText="Saving..."
								disabled={!isValid || !isDirty || isUpdating}
								flex="1"
							>
								Save
							</PrimaryButton>
						</HStack>
					</Box>
				</Box>
			</Box>
			<ConfirmationDialog
				isOpen={isDiscardDialogOpen}
				onOpenChange={setIsDiscardDialogOpen}
				title="Discard changes?"
				message="Confirm that you want to discard unsaved changes."
				confirmText="Discard"
				cancelText="Cancel"
				onConfirm={handleConfirmDiscard}
				onCancel={() => setIsDiscardDialogOpen(false)}
				confirmButtonColorPalette="red"
			/>
		</>
	);
};

export default DocumentNoteSheet;
